import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface PillOption {
  value: string;
  label: string;
  icon?: ReactNode;
  group?: string;
  hint?: string; // secondary text
  disabled?: boolean;
}

interface Props {
  options: PillOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  title?: string;
  menuWidth?: string;
}

/** Compact pill button with an upward-opening menu, for pickers inside the chat input. */
export default function PillSelect({ options, value, onChange, disabled, title, menuWidth = "w-64" }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const groups: [string, PillOption[]][] = [];
  for (const option of options) {
    const name = option.group ?? "";
    const bucket = groups.find(([g]) => g === name);
    if (bucket) bucket[1].push(option);
    else groups.push([name, [option]]);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={title}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex max-w-[200px] items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-secondary-t transition hover:bg-hover-bg hover:text-primary-t disabled:opacity-50 ${
          open ? "bg-hover-bg" : ""
        }`}
      >
        {selected?.icon}
        <span className="truncate">{selected?.label ?? "Select"}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-tertiary-t transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute bottom-full left-0 z-40 mb-2 max-h-80 ${menuWidth} overflow-y-auto rounded-xl border border-border-default-t bg-elevated py-1.5 shadow-lg`}
        >
          {groups.map(([group, items]) => (
            <div key={group || "default"} className="py-0.5">
              {group && (
                <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-tertiary-t">{group}</div>
              )}
              {items.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  disabled={o.disabled}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-primary-t hover:bg-hover-bg disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {o.icon && <span className="flex h-5 w-5 shrink-0 items-center justify-center text-adrsta-blue">{o.icon}</span>}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{o.label}</span>
                    {o.hint && <span className="block truncate text-xs text-tertiary-t">{o.hint}</span>}
                  </span>
                  {o.value === value && <Check className="h-4 w-4 shrink-0 text-adrsta-blue" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
